import React, { type ReactNode } from "react";
import {
  TextInput,
  Textarea,
  List,
  ListItem,
  Group,
  Box,
  NumberInput,
  Button,
  SegmentedControl,
  ColorPicker,
  Text,
  Switch,
  type SwitchProps,
  type TextInputProps,
  type TextareaProps,
  type SegmentedControlProps,
  type ColorPickerProps,
} from "@mantine/core";

import {
  createFormHook,
  createFormHookContexts,
  type StandardSchemaV1Issue,
} from "@tanstack/react-form";

const { fieldContext, formContext, useFieldContext } = createFormHookContexts();

type PotentialError = StandardSchemaV1Issue | string | undefined;

const ErrorMessage: React.FC<{ err: PotentialError }> = (props) => {
  const { err } = props;
  if (!err) {
    return null;
  }

  return (
    <ListItem c="red">{typeof err === "string" ? err : err.message}</ListItem>
  );
};

interface MyTextInputProps extends Omit<TextInputProps, "value" | "onChange"> {
  errors: Array<PotentialError>;
}

export const MyTextInput: React.FC<MyTextInputProps> = (props) => {
  const { errors, ...rest } = props;

  const field = useFieldContext<string>();

  const onlyRealErrors = errors.filter((e) => e !== undefined);

  return (
    <Box mt="md">
      <TextInput
        {...rest}
        value={field.state.value}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
      />

      {errors.length > 0 ? (
        <List>
          {onlyRealErrors.map((err, idx) => (
            <ErrorMessage key={idx} err={err} />
          ))}
        </List>
      ) : null}
    </Box>
  );
};

interface AppTextareaProps extends Omit<TextareaProps, "value" | "onChange"> {
  errors: Array<PotentialError>;
}

const AppTextAreaInput: React.FC<AppTextareaProps> = (props) => {
  const { errors, ...rest } = props;

  const field = useFieldContext<string>();

  const onlyRealErrors = errors.filter((e) => e !== undefined);

  return (
    <Box mt="md">
      <Textarea
        {...rest}
        value={field.state.value}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
        autosize
        minRows={3}
      />

      {errors.length > 0 ? (
        <List>
          {onlyRealErrors.map((err, idx) => (
            <ErrorMessage key={idx} err={err} />
          ))}
        </List>
      ) : null}
    </Box>
  );
};

interface TypedSegmentedControlProps<T extends string>
  extends Omit<SegmentedControlProps, "onChange"> {
  label: string;
  data: T[];
  value?: T;
  defaultValue?: T;
  onChange: (value: T) => void;
}

function TypedSegmentedControl<T extends string>(
  props: TypedSegmentedControlProps<T>,
): ReactNode {
  const { onChange, label, ...restOfProps } = props;

  return (
    <Group mt="md">
      <Text size="sm" fw={500}>
        {label}
      </Text>
      <SegmentedControl
        size="sm"
        {...restOfProps}
        onChange={(untypedVal) => {
          // @ts-expect-error needed to hack typescript here due to following error messagea
          // 'string' is assignable to the constraint of type 'T', but 'T' could be instantiated with a different subtype of constraint 'string'.ts(2430)
          props.onChange(untypedVal);
        }}
      />
    </Group>
  );
}

interface AppColorPickerProps extends ColorPickerProps {
  label: string;
}

const AppColorPicker: React.FC<AppColorPickerProps> = (props) => {
  const { label, ...rest } = props;
  return (
    <Box mt="md">
      <Text size="sm" fw={500} mt="xs">
        {label} ({props.value})
      </Text>
      <ColorPicker {...rest} />
    </Box>
  );
};

interface AppSwitchComponentProps
  extends Omit<SwitchProps, "checked" | "onChange"> {}

const AppSwitchComponent: React.FC<AppSwitchComponentProps> = (props) => {
  const field = useFieldContext<boolean>();
  return (
    <Switch
      mt="md"
      {...props}
      checked={field.state.value}
      onChange={(event) => {
        field.handleChange(event.target.checked);
      }}
    />
  );
};

export const { useAppForm } = createFormHook({
  fieldComponents: {
    TextField: MyTextInput,
    TextareaField: AppTextAreaInput,
    NumberField: NumberInput,
    SegmentedSelectField: TypedSegmentedControl,
    ColorPickerField: AppColorPicker,
    SwitchField: AppSwitchComponent,
  },
  formComponents: {
    SubmitButton: Button.withProps({ type: "submit" }),
  },
  fieldContext,
  formContext,
});
